import { UAParser, type IOS, type IBrowser } from "ua-parser-js"
import { compareVersions } from "compare-versions"

type DesktopOperatingSystem = "Mac OS" | "Windows" | "Linux" | "Chromium OS"
type MobileOperatingSystem = "iOS" | "Android"
type OperatingSystem = DesktopOperatingSystem | MobileOperatingSystem

type DesktopBrowser = "Safari" | "Opera" | "Chrome" | "Firefox" | "Edge"
type MobileBrowser = "Mobile Safari" | "Opera Mini"
type Browser = DesktopBrowser | MobileBrowser

const supported: Partial<
    Record<
        OperatingSystem,
        | {
              osVersion: string
              browsers: Partial<Record<Browser, string | undefined>>
          }
        | undefined
    >
> = {
    "Mac OS": {
        // Ventura
        osVersion: "13.0.0",
        browsers: {
            Chrome: "108",
            Safari: "16.1",
            Firefox: "122",
            Opera: "97",
        },
    },
    Windows: undefined,
    Linux: undefined,
    iOS: {
        osVersion: "16",
        browsers: {
            "Mobile Safari": "16",
            Chrome: "108",
        },
    },
    Android: {
        osVersion: "9",
        browsers: {
            Chrome: "108",
        },
    },
}

const useExperimentalUserAgentData = async () => {
    if ("userAgentData" in navigator) {
        const uad = (navigator as any).userAgentData as { getHighEntropyValues: (opts: any[]) => Promise<any> }
        if ("getHighEntropyValues" in uad) {
            return await uad.getHighEntropyValues(["platform", "platformVersion", "fullVersionList", "mobile"])
        }
    }

    return undefined
}

const legacyUserAgentBasedDetection = () => {
    const runtime = new UAParser(globalThis.navigator.userAgent)
    const browser = runtime.getBrowser() as IBrowser & { name: Browser }
    const os = runtime.getOS() as IOS & { name: OperatingSystem }

    if (!browser.name || !browser.version || !os.name || !os.version) {
        return false
    }

    const minimum = supported[os.name]

    if (!minimum) {
        return false
    }

    const minimumBrowserVersion = minimum?.browsers[browser.name]
    if (!minimumBrowserVersion) {
        return false
    }

    /**
     * We trust that any approved browser on MacOS will work
     *
     * Unfortunately we can't do better targeting on MacOS
     * for the platform version because it always returns
     * 10.15.7
     */
    if (os.name === "Mac OS") {
        return true
    }

    if (compareVersions(browser.version, minimumBrowserVersion) === -1) {
        return false
    }

    return compareVersions(os.version, minimum.osVersion) !== -1
}

const useExperimentalUserAgentDataBasedDetection = (uad: {
    platform: string
    platformVersion: string
    mobile: boolean
    fullVersionList: Array<{ brand: string; version: string }>
}): boolean => {
    const uadBrowserNames = ["Google Chrome", "Microsoft Edge", "Opera"]
    const uadBrandToBrowserName: Record<(typeof uadBrowserNames)[number], Browser> = {
        "Google Chrome": "Chrome",
        "Microsoft Edge": "Edge",
        Opera: "Opera",
    }
    const uadBrandToBrowserNameMobile: Record<(typeof uadBrowserNames)[number], Browser> = {
        "Google Chrome": "Chrome",
        "Microsoft Edge": "Edge",
        Opera: "Opera Mini",
    }
    const uadPlatformToPlatform: Record<string, OperatingSystem> = {
        macOS: "Mac OS",
        Android: "Android",
    }
    const uadBrowser = uad.fullVersionList.find((entry) => {
        return uadBrowserNames.includes(entry.brand)
    })

    if (!uadBrowser || !uad.platform || !uad.platformVersion) {
        return false
    }

    const browserName = uad.mobile
        ? uadBrandToBrowserNameMobile[uadBrowser.brand]
        : uadBrandToBrowserName[uadBrowser.brand]

    const browserVersion = uadBrowser.version
    const platformName = uadPlatformToPlatform[uad.platform]

    const minimum = supported[platformName]

    if (!minimum) {
        return false
    }

    if (compareVersions(uad.platformVersion, minimum.osVersion) === -1) {
        return false
    }

    const minimumBrowserVersion = minimum?.browsers[browserName]
    if (!minimumBrowserVersion) {
        return false
    }

    return compareVersions(browserVersion, minimumBrowserVersion) !== -1
}

export const runtimeSupportsBestPasskeyUX = async (): Promise<boolean> => {
    try {
        const experimentalUserAgentData = await useExperimentalUserAgentData()

        if (experimentalUserAgentData) {
            return useExperimentalUserAgentDataBasedDetection(experimentalUserAgentData)
        }

        return legacyUserAgentBasedDetection()
    } catch {
        return false
    }
}
